import type { Paise, RateWindow, StationType } from '@/types/domain';
import { dayOfWeek, minutesOfDay } from './dates';

/**
 * Safety-net rates (paise/min) if no rate window matches. These mirror the
 * seeded demo rates but the seeded RateWindows are the real source of truth —
 * administrators edit those, not this constant.
 */
export const DEFAULT_RATE_PAISE: Record<StationType, Paise> = {
  POOL: 300,
  MINI_SNOOKER: 350,
  SNOOKER: 450,
};

function windowCoversMinute(win: RateWindow, minute: number): boolean {
  if (win.startMinute === win.endMinute) return true; // full day
  if (win.startMinute < win.endMinute) {
    return minute >= win.startMinute && minute < win.endMinute;
  }
  // Overnight window, e.g. 22:00 → 02:00
  return minute >= win.startMinute || minute < win.endMinute;
}

/** A bounded window (real start/end) is more specific than an all-day one. */
function isFullDay(win: RateWindow): boolean {
  return win.startMinute === win.endMinute;
}

/**
 * Resolve the per-minute rate (paise) for a station type at a given instant.
 * Pure and deterministic — no clocks, no I/O. Components never call this; the
 * billing service and useRateWindows do.
 *
 * A specific time-of-day window (e.g. an evening peak) always wins over a
 * full-day base window, regardless of the order they appear in the array —
 * otherwise a base "all day" rate listed first would shadow every peak window.
 */
export function resolveRatePaise(
  windows: RateWindow[],
  type: StationType,
  at: Date | string = new Date(),
): Paise {
  const minute = minutesOfDay(at);
  const dow = dayOfWeek(at);

  const candidates = windows
    .filter((w) => w.active && w.stationType === type)
    .filter((w) => w.daysOfWeek.length === 0 || w.daysOfWeek.includes(dow))
    .filter((w) => windowCoversMinute(w, minute));

  // Prefer a specific (bounded) window; fall back to a full-day window.
  const match = candidates.find((w) => !isFullDay(w)) ?? candidates.find(isFullDay);

  return match ? match.ratePerMinutePaise : DEFAULT_RATE_PAISE[type];
}
